'use client';

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiBase } from '@/lib/api';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';

type MyMemberResponse = {
  exists: boolean;
  member: null | {
    id: string;
    displayName: string;
    membershipNumber: string | null;
  };
};

type FrequencyType = 'regular' | 'irregular';
type RegularPattern = 'monthly' | 'biweekly' | 'other';
type LocationType = 'online' | 'offline' | 'hybrid';
type FeeType = 'free' | 'paid';

export default function CreateClubPage() {
  const { token, loading: authLoading } = useAuth();
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberInfo, setMemberInfo] = useState<MyMemberResponse['member']>(null);
  const [memberLoading, setMemberLoading] = useState(false);

  const todayISO = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const [form, setForm] = useState({
    // 1. 基本情報
    clubName: '',
    viceLeadersSupport: '',

    // 2. 部長経歴
    leaderHistory: '',

    // 3. 部活の概要
    targetAudience: '',
    offering: '',
    clubGoal: '',

    // 4. 運営方法について
    frequencyType: 'regular' as FrequencyType,
    regularPattern: 'monthly' as RegularPattern,
    regularOtherText: '',
    irregularReasonAndFrequency: '',
    schedule: '',
    locationType: 'online' as LocationType,
    onlineTool: '',
    offlinePlace: '',
    participantsMin: '',
    participantsMax: '',

    // 5. 会費・費用について
    feeType: 'free' as FeeType,
    feePerSessionYen: '',
    feeMonthlyYen: '',
    feeOther: '',
    feeReason: '',

    // 6. 継続運営のための考え方
    sustainability: '',

    // 7. 自己PR（部長として）
    selfPR: '',

    // 8. リスク・注意点（任意）
    risks: '',

    // 9. 補足事項（任意）
    notes: '',

    // 申請日・署名
    applicationDate: todayISO,
    signature: '',

    // 部長として「問い合わせメールが自分のメールアドレスに届く」ことへの同意
    leaderContactConsent: false,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const fetchMyMember = async () => {
      setMemberLoading(true);
      try {
        const res = await fetch(`${apiBase()}/api/auth/my/member`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('プロフィール情報の取得に失敗しました');
        const data = (await res.json()) as MyMemberResponse;
        if (cancelled) return;
        setMemberInfo(data.member);
        // 署名はデフォルトで部長名を入れておく（編集可）
        if (data.member?.displayName) {
          setForm((prev) => ({
            ...prev,
            signature: prev.signature || data.member!.displayName,
          }));
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setMemberInfo(null);
      } finally {
        if (!cancelled) setMemberLoading(false);
      }
    };

    fetchMyMember();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const buildDerivedDescription = () => {
    // 公開ページに出る可能性のあるdescriptionは、申請書(3)の内容から生成する
    return [
      `【誰向けの部活か】\n${form.targetAudience}`,
      `【提供内容】\n${form.offering}`,
      `【この部活を通して目指すゴール】\n${form.clubGoal}`,
    ].join('\n\n');
  };

  const buildActivityDay = () => {
    const schedule = form.schedule ? ` / ${form.schedule}` : '';
    if (form.frequencyType === 'regular') {
      const label =
        form.regularPattern === 'monthly'
          ? '毎月'
          : form.regularPattern === 'biweekly'
            ? '隔週'
            : `その他: ${form.regularOtherText || '（未記入）'}`;
      return `定期開催（${label}）${schedule}`.trim();
    }
    return `不定期開催（理由・目安頻度: ${form.irregularReasonAndFrequency || '（未記入）'}）${schedule}`.trim();
  };

  const buildActivityPlace = () => {
    if (form.locationType === 'online') {
      return `オンライン（使用予定ツール: ${form.onlineTool || '（未記入）'}）`;
    }
    if (form.locationType === 'offline') {
      return `オフライン（場所: ${form.offlinePlace || '（未記入）'}）`;
    }
    const online = `オンライン: ${form.onlineTool || '（未記入）'}`;
    const offline = `オフライン: ${form.offlinePlace || '（未記入）'}`;
    return `ハイブリッド（${online} / ${offline}）`;
  };

  const validateBeforeSubmit = () => {
    if (!memberInfo) return '創部申請にはメンバープロフィールの登録が必要です。';
    if (!form.clubName.trim()) return '部活名を入力してください。';
    if (!form.leaderHistory.trim()) return '部長経歴を入力してください。';
    if (!form.targetAudience.trim()) return '「誰向けの部活か」を入力してください。';
    if (!form.offering.trim()) return '「どんなことを提供する部活か」を入力してください。';
    if (!form.clubGoal.trim()) return '「この部活を通して目指すゴール」を入力してください。';
    if (form.frequencyType === 'regular' && form.regularPattern === 'other' && !form.regularOtherText.trim()) {
      return '開催頻度の「その他」を選んだ場合は内容を入力してください。';
    }
    if (form.frequencyType === 'irregular' && !form.irregularReasonAndFrequency.trim()) {
      return '不定期開催を選んだ場合は「理由・目安頻度」を入力してください。';
    }
    if (!form.schedule.trim()) return '開催日時（曜日・時間帯・所要時間の目安）を入力してください。';
    if (form.locationType === 'online' && !form.onlineTool.trim()) return 'オンラインの場合は使用予定ツールを入力してください。';
    if (form.locationType === 'offline' && !form.offlinePlace.trim()) return 'オフラインの場合は場所を入力してください。';
    if (form.locationType === 'hybrid' && (!form.onlineTool.trim() || !form.offlinePlace.trim())) {
      return 'ハイブリッドの場合はオンライン/オフライン両方の情報を入力してください。';
    }
    const min = form.participantsMin.trim() ? Number(form.participantsMin) : null;
    const max = form.participantsMax.trim() ? Number(form.participantsMax) : null;
    if (min !== null && (!Number.isFinite(min) || min < 0)) return '想定参加人数（最少）は0以上の数字で入力してください。';
    if (max !== null && (!Number.isFinite(max) || max < 0)) return '想定参加人数（最大）は0以上の数字で入力してください。';
    if (min !== null && max !== null && min > max) return '想定参加人数は「最少 ≤ 最大」になるよう入力してください。';

    if (form.feeType === 'paid') {
      const per = form.feePerSessionYen.trim() ? Number(form.feePerSessionYen) : null;
      const monthly = form.feeMonthlyYen.trim() ? Number(form.feeMonthlyYen) : null;
      if (per === null && monthly === null && !form.feeOther.trim()) {
        return '有料の場合は「1回あたり」「月額制」「その他」のいずれかを入力してください。';
      }
      if (per !== null && (!Number.isFinite(per) || per < 0)) return '1回あたり金額は0以上の数字で入力してください。';
      if (monthly !== null && (!Number.isFinite(monthly) || monthly < 0)) return '月額制金額は0以上の数字で入力してください。';
      if (!form.feeReason.trim()) return '金額設定の理由を入力してください。';
    }

    if (!form.sustainability.trim()) return '継続運営のための考え方を入力してください。';
    if (!form.selfPR.trim()) return '自己PR（部長として）を入力してください。';
    if (!form.applicationDate.trim()) return '申請日を入力してください。';
    if (!form.signature.trim()) return '申請者署名を入力してください。';
    if (!form.leaderContactConsent) {
      return '部長として「問い合わせメールを受信する」ことへの同意が必要です。';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (!token) {
        throw new Error('ログインが必要です。');
      }
      const validationError = validateBeforeSubmit();
      if (validationError) {
        setError(validationError);
        setSubmitting(false);
        return;
      }

      const applicationPayload = {
        basic: {
          clubName: form.clubName,
          leaderName: memberInfo?.displayName ?? '',
          membershipNumber: memberInfo?.membershipNumber ?? null,
          viceLeadersSupport: form.viceLeadersSupport || null,
          leaderContactConsent: form.leaderContactConsent,
        },
        leaderHistory: form.leaderHistory,
        overview: {
          targetAudience: form.targetAudience,
          offering: form.offering,
          goal: form.clubGoal,
        },
        operation: {
          frequencyType: form.frequencyType,
          regular: form.frequencyType === 'regular'
            ? {
                pattern: form.regularPattern,
                otherText: form.regularPattern === 'other' ? form.regularOtherText : null,
              }
            : null,
          irregular: form.frequencyType === 'irregular'
            ? {
                reasonAndFrequency: form.irregularReasonAndFrequency,
              }
            : null,
          schedule: form.schedule,
          location: {
            type: form.locationType,
            onlineTool: form.locationType !== 'offline' ? (form.onlineTool || null) : null,
            offlinePlace: form.locationType !== 'online' ? (form.offlinePlace || null) : null,
          },
          participants: {
            min: form.participantsMin.trim() ? Number(form.participantsMin) : null,
            max: form.participantsMax.trim() ? Number(form.participantsMax) : null,
          },
        },
        fees: {
          type: form.feeType,
          perSessionYen: form.feePerSessionYen.trim() ? Number(form.feePerSessionYen) : null,
          monthlyYen: form.feeMonthlyYen.trim() ? Number(form.feeMonthlyYen) : null,
          other: form.feeOther || null,
          reason: form.feeType === 'paid' ? form.feeReason : null,
        },
        sustainability: form.sustainability,
        selfPR: form.selfPR,
        risks: form.risks || null,
        notes: form.notes || null,
        application: {
          date: form.applicationDate,
          signature: form.signature,
        },
      };

      const payload = {
        name: form.clubName,
        description: buildDerivedDescription(),
        activityDay: buildActivityDay(),
        activityPlace: buildActivityPlace(),
        applicationPayload,
      };

      const res = await fetch(`${apiBase()}/api/auth/clubs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || JSON.stringify(data.errors) || 'エラーが発生しました');
      }

      alert('創部申請を受け付けました！\n承認されるまでお待ちください。');
      router.push('/clubs');
    } catch (e: any) {
      console.error(e);
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Hooksは常に同じ順番で呼ぶ必要があるため、条件分岐によるreturnはここで行う
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className='min-h-screen bg-white'>
        <Header />
        <div className='flex flex-col items-center justify-center py-20 px-6 text-center'>
          <h1 className='text-2xl font-bold mb-4'>部活の創部にはログインが必要です</h1>
          <a
            href='/login?redirect=/clubs/create'
            className='rounded-full bg-primary px-6 py-3 text-white font-bold'
          >
            ログインする
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-white'>
      <Header />
      
      <main className='bg-gray-50 py-12'>
        <div className='mx-auto max-w-2xl px-6'>
          <h1 className='text-3xl font-serif font-bold text-gray-900 mb-2'>部活を立ち上げる</h1>
          <p className='text-gray-600 mb-8'>必要事項を入力して申請してください。事務局の承認後に公開されます。</p>

          <div className='bg-white shadow-sm p-8'>
            {error && (
              <div className='mb-6 rounded-lg bg-rose-50 p-4 text-rose-700 text-sm'>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              {/* 1. 基本情報 */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>1. 基本情報</h2>
                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>
                    部活名 <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    type='text'
                    required
                    value={form.clubName}
                    onChange={e => setForm(prev => ({ ...prev, clubName: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    placeholder='例: 週末登山部'
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>部長氏名</label>
                    <input
                      type='text'
                      value={memberLoading ? '取得中...' : (memberInfo?.displayName ?? '')}
                      disabled
                      className='w-full border border-gray-200 bg-gray-50 px-4 py-2 text-gray-700'
                    />
                    <p className='mt-1 text-xs text-gray-500'>※ログイン中のプロフィール情報を使用します</p>
                  </div>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>会員ナンバー</label>
                    <input
                      type='text'
                      value={memberLoading ? '取得中...' : (memberInfo?.membershipNumber ?? '')}
                      disabled
                      className='w-full border border-gray-200 bg-gray-50 px-4 py-2 text-gray-700'
                    />
                    <p className='mt-1 text-xs text-gray-500'>※ログイン中のプロフィール情報を使用します</p>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>副部長・運営サポート（いる場合）</label>
                  <input
                    type='text'
                    value={form.viceLeadersSupport}
                    onChange={e => setForm(prev => ({ ...prev, viceLeadersSupport: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    placeholder='例: 副部長: 山田太郎 / 運営: 佐藤花子'
                  />
                </div>
              </div>

              {/* 2. 部長経歴 */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>2. 部長経歴</h2>
                <p className='text-sm text-gray-600'>※これまでの職歴・活動内容・専門分野・実績などを簡潔にご記入ください。</p>
                <textarea
                  required
                  rows={5}
                  value={form.leaderHistory}
                  onChange={e => setForm(prev => ({ ...prev, leaderHistory: e.target.value }))}
                  className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                />
              </div>

              {/* 3. 部活の概要 */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>3. 部活の概要</h2>
                <p className='text-xs text-gray-500'>
                  ※ 本文中に <code>https://...</code> 形式のURLを入れると、部活詳細ページで自動的にクリック可能なリンクとして表示されます。
                </p>
                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>
                    （1）誰向けの部活か <span className='text-rose-500'>*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={form.targetAudience}
                    onChange={e => setForm(prev => ({ ...prev, targetAudience: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    placeholder='例: 起業初期の方、個人事業主、経営者、特定テーマに関心のある方 など'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>
                    （2）どんなことを提供する部活か <span className='text-rose-500'>*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={form.offering}
                    onChange={e => setForm(prev => ({ ...prev, offering: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    placeholder='例: 勉強会／交流／実践／相談／作業会 など'
                  />
                </div>
                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>
                    （3）この部活を通して目指すゴール <span className='text-rose-500'>*</span>
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={form.clubGoal}
                    onChange={e => setForm(prev => ({ ...prev, clubGoal: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                  />
                </div>
              </div>

              {/* 4. 運営方法について */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>4. 運営方法について</h2>

                <div className='space-y-2'>
                  <label className='block text-sm font-bold text-gray-700'>
                    （1）開催頻度・定期性 <span className='text-rose-500'>*</span>
                  </label>
                  <div className='space-y-2'>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='frequencyType'
                        checked={form.frequencyType === 'regular'}
                        onChange={() => setForm(prev => ({ ...prev, frequencyType: 'regular' }))}
                      />
                      定期開催
                    </label>
                    {form.frequencyType === 'regular' && (
                      <div className='ml-6 grid grid-cols-1 md:grid-cols-2 gap-3'>
                        <select
                          value={form.regularPattern}
                          onChange={(e) => setForm(prev => ({ ...prev, regularPattern: e.target.value as RegularPattern }))}
                          className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                        >
                          <option value='monthly'>毎月</option>
                          <option value='biweekly'>隔週</option>
                          <option value='other'>その他</option>
                        </select>
                        <input
                          type='text'
                          value={form.regularOtherText}
                          onChange={e => setForm(prev => ({ ...prev, regularOtherText: e.target.value }))}
                          disabled={form.regularPattern !== 'other'}
                          className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                          placeholder='その他の内容'
                        />
                      </div>
                    )}

                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='frequencyType'
                        checked={form.frequencyType === 'irregular'}
                        onChange={() => setForm(prev => ({ ...prev, frequencyType: 'irregular' }))}
                      />
                      不定期開催
                    </label>
                    {form.frequencyType === 'irregular' && (
                      <div className='ml-6'>
                        <input
                          type='text'
                          value={form.irregularReasonAndFrequency}
                          onChange={e => setForm(prev => ({ ...prev, irregularReasonAndFrequency: e.target.value }))}
                          className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                          placeholder='理由・目安頻度'
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>
                    （2）開催日時 <span className='text-rose-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={form.schedule}
                    onChange={e => setForm(prev => ({ ...prev, schedule: e.target.value }))}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    placeholder='例: 毎月第3土曜 10:00-12:00（2時間）'
                  />
                </div>

                <div className='space-y-2'>
                  <label className='block text-sm font-bold text-gray-700'>
                    （3）開催場所・方法 <span className='text-rose-500'>*</span>
                  </label>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='locationType'
                        checked={form.locationType === 'online'}
                        onChange={() => setForm(prev => ({ ...prev, locationType: 'online' }))}
                      />
                      オンライン
                    </label>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='locationType'
                        checked={form.locationType === 'offline'}
                        onChange={() => setForm(prev => ({ ...prev, locationType: 'offline' }))}
                      />
                      オフライン
                    </label>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='locationType'
                        checked={form.locationType === 'hybrid'}
                        onChange={() => setForm(prev => ({ ...prev, locationType: 'hybrid' }))}
                      />
                      ハイブリッド
                    </label>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div>
                      <label className='block text-sm font-bold text-gray-700 mb-1'>オンライン（使用予定ツール）</label>
                      <input
                        type='text'
                        value={form.onlineTool}
                        onChange={e => setForm(prev => ({ ...prev, onlineTool: e.target.value }))}
                        disabled={form.locationType === 'offline'}
                        className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                        placeholder='例: Zoom / Google Meet'
                      />
                    </div>
                    <div>
                      <label className='block text-sm font-bold text-gray-700 mb-1'>オフライン（場所）</label>
                      <input
                        type='text'
                        value={form.offlinePlace}
                        onChange={e => setForm(prev => ({ ...prev, offlinePlace: e.target.value }))}
                        disabled={form.locationType === 'online'}
                        className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                        placeholder='例: 都内のカフェ / 会議室'
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>（4）1回あたりの想定参加人数</label>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>最少</label>
                      <input
                        type='number'
                        min={0}
                        value={form.participantsMin}
                        onChange={e => setForm(prev => ({ ...prev, participantsMin: e.target.value }))}
                        className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                        placeholder='例: 3'
                      />
                    </div>
                    <div>
                      <label className='block text-xs text-gray-500 mb-1'>最大</label>
                      <input
                        type='number'
                        min={0}
                        value={form.participantsMax}
                        onChange={e => setForm(prev => ({ ...prev, participantsMax: e.target.value }))}
                        className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                        placeholder='例: 12'
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. 会費・費用について */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>5. 会費・費用について</h2>

                <div className='space-y-2'>
                  <label className='block text-sm font-bold text-gray-700'>（1）参加費・会費の有無</label>
                  <div className='flex gap-6'>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='feeType'
                        checked={form.feeType === 'free'}
                        onChange={() => setForm(prev => ({ ...prev, feeType: 'free' }))}
                      />
                      無料
                    </label>
                    <label className='flex items-center gap-2 text-sm text-gray-700'>
                      <input
                        type='radio'
                        name='feeType'
                        checked={form.feeType === 'paid'}
                        onChange={() => setForm(prev => ({ ...prev, feeType: 'paid' }))}
                      />
                      有料
                    </label>
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>（2）金額設定：1回あたり（円）</label>
                    <input
                      type='number'
                      min={0}
                      value={form.feePerSessionYen}
                      onChange={e => setForm(prev => ({ ...prev, feePerSessionYen: e.target.value }))}
                      disabled={form.feeType !== 'paid'}
                      className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                      placeholder='例: 1000'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>（2）金額設定：月額制（円）</label>
                    <input
                      type='number'
                      min={0}
                      value={form.feeMonthlyYen}
                      onChange={e => setForm(prev => ({ ...prev, feeMonthlyYen: e.target.value }))}
                      disabled={form.feeType !== 'paid'}
                      className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                      placeholder='例: 3000'
                    />
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>（2）その他（内容）</label>
                  <input
                    type='text'
                    value={form.feeOther}
                    onChange={e => setForm(prev => ({ ...prev, feeOther: e.target.value }))}
                    disabled={form.feeType !== 'paid'}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                    placeholder='例: 材料費実費 / スポンサー募集 など'
                  />
                </div>

                <div>
                  <label className='block text-sm font-bold text-gray-700 mb-1'>（3）金額設定の理由</label>
                  <textarea
                    rows={3}
                    value={form.feeReason}
                    onChange={e => setForm(prev => ({ ...prev, feeReason: e.target.value }))}
                    disabled={form.feeType !== 'paid'}
                    className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50'
                    placeholder='参加者への価値、運営コスト、継続性の観点からご記入ください。'
                  />
                </div>
              </div>

              {/* 6. 継続運営 */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>6. 継続運営のための考え方</h2>
                <textarea
                  required
                  rows={4}
                  value={form.sustainability}
                  onChange={e => setForm(prev => ({ ...prev, sustainability: e.target.value }))}
                  className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                  placeholder='無理なく続けるための工夫、部長としての関わり方、今後の展望など'
                />
              </div>

              {/* 7. 自己PR */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>7. 自己PR（部長として）</h2>
                <textarea
                  required
                  rows={4}
                  value={form.selfPR}
                  onChange={e => setForm(prev => ({ ...prev, selfPR: e.target.value }))}
                  className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                  placeholder='ご自身の強み、運営で大切にしたいスタンス、参加者へのメッセージ'
                />
              </div>

              {/* 8. リスク・注意点（任意） */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>8. リスク・注意点の想定（任意）</h2>
                <textarea
                  rows={3}
                  value={form.risks}
                  onChange={e => setForm(prev => ({ ...prev, risks: e.target.value }))}
                  className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                  placeholder='トラブル防止のために想定している点、配慮事項など'
                />
              </div>

              {/* 9. 補足事項（任意） */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>9. 補足事項（任意）</h2>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                  placeholder='その他、運営側に共有しておきたいこと'
                />
              </div>

              {/* 申請日・署名 */}
              <div className='space-y-4 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>申請日・署名</h2>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>
                      申請日 <span className='text-rose-500'>*</span>
                    </label>
                    <input
                      type='date'
                      value={form.applicationDate}
                      onChange={e => setForm(prev => ({ ...prev, applicationDate: e.target.value }))}
                      className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-bold text-gray-700 mb-1'>
                      申請者署名 <span className='text-rose-500'>*</span>
                    </label>
                    <input
                      type='text'
                      value={form.signature}
                      onChange={e => setForm(prev => ({ ...prev, signature: e.target.value }))}
                      className='w-full border border-gray-300 px-4 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                      placeholder='例: 山田太郎'
                    />
                  </div>
                </div>
              </div>

              {/* 部長の同意（問い合わせ受信） */}
              <div className='space-y-2 border border-gray-200 p-4'>
                <h2 className='text-lg font-bold text-gray-900'>同意事項</h2>
                <label className='flex items-start gap-3 text-sm text-gray-700'>
                  <input
                    type='checkbox'
                    checked={form.leaderContactConsent}
                    onChange={(e) => setForm(prev => ({ ...prev, leaderContactConsent: e.target.checked }))}
                    className='mt-1 h-4 w-4 accent-primary'
                  />
                  <span>
                    部長として、会員からの問い合わせが<strong className='font-bold'>自分のメールアドレス宛</strong>に届くことを了承します。
                  </span>
                </label>
                <p className='text-xs text-gray-500'>
                  ※問い合わせが届いた後は、送信者と直接メールでやり取りしてください。
                </p>
              </div>

              <div className='pt-6'>
                <button
                  type='submit'
                  disabled={submitting}
                  className='w-full rounded-full bg-primary py-3 font-bold text-white shadow-lg hover:bg-primary/90 disabled:opacity-70'
                >
                  {submitting ? (
                    <span className='flex items-center justify-center gap-2'>
                      <Loader2 className='animate-spin' /> 申請中...
                    </span>
                  ) : (
                    '申請する'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

